import { appPath, workAreaRoutePath } from './addressing/paths.js';
import type { IPolicyRuleSource } from './work-area-rule-sources.js';

/**
 * T-0824 (I-1914): the places a refusal may name, as data. The server sets one of these ids beside
 * a refusal's sentence (`destination`), and the web links it through the refusal destination with
 * the reader's own access; a refusal without an id keeps its text alone. No surface finds a place by
 * reading the sentence's English.
 *
 * `scope` says which settings page holds it: a work area's (`?section=` of its settings) or the
 * installation's (Global settings). `section` is that page's `?section=` id.
 */
export const REFUSAL_DESTINATION_IDS = [
  'workflow.allowed-moves',
  'workflow',
  'policy',
  'work-item-defaults',
  'item-types',
  'external-access',
  'email',
  'agents',
  'billing',
  'delete-account',
  'roles',
  'streams',
] as const;
export type RefusalDestinationIdType = (typeof REFUSAL_DESTINATION_IDS)[number];

export interface IRefusalDestinationPlace {
  scope: 'work-area' | 'global';
  section: string;
  label: string;
  place: string;
  /**
   * ASF-19: `label` and `place` as catalogue keys, the ones the settings rail and the sidebar draw,
   * so a refusal names the entry the reader sees, in their language. `label` and `place` are those
   * keys' English (`refusal-destination-catalogue.test.ts`).
   */
  labelKey: string;
  placeKeys: readonly string[];
}

const WORK_AREA_SETTINGS = 'navigation.account.workAreaSettings';
const GLOBAL_SETTINGS = 'navigation.account.globalSettings';

/** A place one section deep: the settings page, then the section. */
function section(
  scope: IRefusalDestinationPlace['scope'],
  id: string,
  label: string,
  key: string,
): IRefusalDestinationPlace {
  // F-0777: both pages are named Settings; the work area's is a group of the one Settings entry.
  const settings = 'Settings';
  return {
    scope,
    section: id,
    label,
    place: `${settings} → ${label}`,
    labelKey: `settingsSections.${key}`,
    placeKeys: [
      scope === 'work-area' ? WORK_AREA_SETTINGS : GLOBAL_SETTINGS,
      `settingsSections.${key}`,
    ],
  };
}

export const REFUSAL_DESTINATION_PLACES: Readonly<
  Record<RefusalDestinationIdType, IRefusalDestinationPlace>
> = {
  'workflow.allowed-moves': {
    scope: 'work-area',
    section: 'workflow',
    label: 'Allowed moves',
    place: 'Settings → Workflow → Allowed moves',
    labelKey: 'settingsSections.allowedMoves',
    placeKeys: [WORK_AREA_SETTINGS, 'settingsSections.workflow', 'settingsSections.allowedMoves'],
  },
  workflow: section('work-area', 'workflow', 'Workflow', 'workflow'),
  policy: section('work-area', 'policies', 'Policies', 'policies'),
  'work-item-defaults': section(
    'global',
    'work-item-defaults',
    'Work-item defaults',
    'workItemDefaults',
  ),
  // The section Global settings draws at `?section=item-types`, where its link goes.
  'item-types': section('global', 'item-types', 'Item types', 'itemTypes'),
  'external-access': section('work-area', 'external', 'External access', 'externalAccess'),
  email: section('global', 'email', 'Email', 'email'),
  // A work-area place is the work area the refusal is about: for a move, the target.
  agents: section('work-area', 'agents', 'Agents', 'agents'),
  billing: section('global', 'billing', 'Billing', 'billing'),
  'delete-account': section('global', 'delete-account', 'Delete account', 'deleteAccount'),
  roles: section('global', 'roles', 'Roles & permissions', 'roles'),
  streams: section('work-area', 'streams', 'Streams', 'streams'),
};

export function isRefusalDestinationId(value: unknown): value is RefusalDestinationIdType {
  return (REFUSAL_DESTINATION_IDS as readonly unknown[]).includes(value);
}

/** The id a refusal's body names (`destination`), or null for none or one this build does not know. */
export function refusalDestinationIdOf(payload: unknown): RefusalDestinationIdType | null {
  if (!payload || typeof payload !== 'object') return null;
  const destination = (payload as { destination?: unknown }).destination;
  return isRefusalDestinationId(destination) ? destination : null;
}

/** The app path of a place: the work area's settings section, or Global settings'. */
export function refusalDestinationPath(
  id: RefusalDestinationIdType,
  workAreaRef: string | null | undefined,
): string {
  const { scope, section } = REFUSAL_DESTINATION_PLACES[id];
  const settings =
    scope === 'work-area' && workAreaRef
      ? workAreaRoutePath(workAreaRef, '/settings')
      : appPath('/settings');
  return `${settings}?section=${section}`;
}

/**
 * Where the layer that stated a rule is changed (`policyRuleSourceSentence`'s first place), or null
 * for the built-in default, which no setting changes.
 */
export function policyRuleSourceDestination(
  source: IPolicyRuleSource | null | undefined,
): RefusalDestinationIdType | null {
  switch (source?.layer) {
    case 'global-default':
      return 'work-item-defaults';
    case 'item-type':
      return 'item-types';
    case 'template':
    case 'work-area':
    case 'work-area-item-type':
      return 'policy';
    default:
      return null;
  }
}
